import Icon from '@/components/shared/icons'
import AttributeProductEntity from '@/entities/api/products/attributes'
import { NameAttributes } from '@/entities/api/products/name-attributes'
import nameAttributeConvert from '@/utils/name-attributes-convert'
import formatCurrency from '@/utils/price-convert'
import {
	Badge,
	Box,
	Button,
	Flex,
	Heading,
	Image,
	ListItem,
	Text,
} from '@chakra-ui/react'

const AttributeItem = ({
	url,
	attribute,
	deleteAttribute,
	onEdit,
}: {
	url: string
	attribute: Partial<AttributeProductEntity>
	deleteAttribute: (id: string) => void
	onEdit?: () => void
}) => {
	const renderAttributeGroup = () => {
		const names = Array.isArray(attribute.name)
			? attribute.name
			: [attribute.name].filter(Boolean)
		const values = Array.isArray(attribute.value)
			? attribute.value
			: [attribute.value].filter(Boolean)
		const references = Array.isArray(attribute.reference)
			? attribute.reference
			: [attribute.reference].filter(Boolean)

		const maxLength = Math.max(names.length, values.length, references.length)
		const attributeItems = []

		for (let i = 0; i < maxLength; i++) {
			attributeItems.push({
				name: names[i] || NameAttributes.COLOR,
				value: values[i] || 'Sin valor',
				reference: references[i] || 'Sin referencia',
			})
		}

		return attributeItems
	}

	const attributeItems = renderAttributeGroup()
	const hasMultipleAttributes = attributeItems.length > 1

	const getMainTitle = () => {
		if (attributeItems.length === 0) return 'Atributo sin datos'

		const uniqueTypes = [
			...new Set(attributeItems.map((item) => nameAttributeConvert(item.name))),
		]

		if (uniqueTypes.length === 1) {
			const values = attributeItems.map((item) => item.value).join(', ')
			return `${uniqueTypes[0]} - ${values}`
		} else {
			return uniqueTypes.join(' + ')
		}
	}

	const isMobile =
		typeof window !== 'undefined' ? window.innerWidth < 768 : false
	const isTablet =
		typeof window !== 'undefined'
			? window.innerWidth >= 768 && window.innerWidth < 1024
			: false
	const maxAttributesToShow = isMobile ? 2 : isTablet ? 4 : 6

	return (
		<ListItem
			display='flex'
			flexDirection={{ base: 'column', md: 'row' }}
			alignItems={{ base: 'stretch', md: 'center' }}
			p={{ base: '4', md: '3' }}
			borderBottom='1px solid'
			borderColor='gray.200'
			_hover={{ bg: 'gray.50' }}
			transition='background 0.2s'
			borderRadius='md'
			gap={{ base: '3', md: '0' }}
		>
			<Flex
				width='full'
				gap='3'
				alignItems={{ base: 'flex-start', md: 'center' }}
			>
				<Box borderRadius='md' overflow='hidden' flexShrink={0}>
					<Image
						src={url}
						alt='Imagen del atributo'
						boxSize={{ base: '60px', md: '70px' }}
						objectFit='cover'
					/>
				</Box>

				<Flex flex='1' direction='column' overflow='hidden' minW={0}>
					<Heading
						as='h5'
						fontSize={{ base: 'md', md: 'lg' }}
						fontWeight='700'
						color='gray.800'
						mb='2'
						lineHeight='1.2'
						overflow='hidden'
						textOverflow='ellipsis'
						whiteSpace='nowrap'
					>
						{getMainTitle()}
					</Heading>

					<Flex gap='4' mb={hasMultipleAttributes ? '3' : '0'} flexWrap='wrap'>
						<Flex alignItems='center' minW='fit-content'>
							<Text fontSize='xs' color='gray.600' mr='2' fontWeight='500'>
								Cantidad:
							</Text>
							<Badge colorScheme='green' fontSize='xs' px='2' py='1'>
								{attribute.quantity || 0}
							</Badge>
						</Flex>

						<Flex alignItems='center' minW='fit-content'>
							<Text fontSize='xs' color='gray.600' mr='2' fontWeight='500'>
								Precio:
							</Text>
							<Badge
								colorScheme='purple'
								fontSize='xs'
								px='2'
								py='1'
								maxW={{ base: '120px', md: '200px' }}
								overflow='hidden'
								textOverflow='ellipsis'
								whiteSpace='nowrap'
							>
								{formatCurrency(attribute.price || 0)}
							</Badge>
						</Flex>
					</Flex>

					{hasMultipleAttributes && (
						<Flex direction='column' gap='2'>
							<Box h='1px' bg='gray.300' w='full' />

							<Text fontSize='xs' color='gray.500' fontWeight='600' mb='1'>
								Detalles de atributos:
							</Text>

							<Flex flexWrap='wrap' gap='2' alignItems='center'>
								{attributeItems
									.slice(0, maxAttributesToShow)
									.map((item, index) => (
										<Flex
											key={index}
											direction={{ base: 'column', sm: 'row' }}
											align={{ base: 'flex-start', sm: 'center' }}
											bg='gray.100'
											borderRadius='md'
											p='2'
											gap='1'
											minW={{ base: 'full', sm: 'fit-content' }}
										>
											<Text
												fontSize='xs'
												fontWeight='600'
												color='gray.700'
												minW='fit-content'
											>
												{nameAttributeConvert(item.name)}:
											</Text>
											<Flex
												direction='column'
												gap='1'
												align={{ base: 'flex-start', sm: 'center' }}
											>
												<Badge
													colorScheme='blue'
													size='sm'
													maxW='100px'
													overflow='hidden'
													textOverflow='ellipsis'
													whiteSpace='nowrap'
												>
													{item.value}
												</Badge>
												{!isMobile && (
													<Text fontSize='10px' color='gray.500'>
														Ref: {item.reference}
													</Text>
												)}
											</Flex>
										</Flex>
									))}

								{attributeItems.length > maxAttributesToShow && (
									<Badge
										colorScheme='gray'
										variant='outline'
										fontSize='xs'
										px='2'
										py='1'
									>
										+{attributeItems.length - maxAttributesToShow} más
									</Badge>
								)}
							</Flex>
						</Flex>
					)}

					{!hasMultipleAttributes && attributeItems.length > 0 && (
						<Flex alignItems='center' mt='1'>
							<Text fontSize='xs' color='gray.600' mr='2' fontWeight='500'>
								Ref:
							</Text>
							<Badge
								colorScheme='blue'
								fontSize='xs'
								px='2'
								py='1'
								maxW={{ base: '120px', md: '200px' }}
								overflow='hidden'
								textOverflow='ellipsis'
								whiteSpace='nowrap'
							>
								{attributeItems[0].reference}
							</Badge>
						</Flex>
					)}
				</Flex>

				<Flex
					direction={{ base: 'row', md: 'column' }}
					gap='1'
					flexShrink={0}
					alignSelf={{ base: 'flex-end', md: 'center' }}
					ml={{ base: '0', md: 'auto' }}
				>
					{onEdit && (
						<Button
							aria-label='Editar atributo'
							variant='ghost'
							colorScheme='blue'
							size='sm'
							borderRadius='full'
							p='2'
							minW='auto'
							h='auto'
							onClick={onEdit}
						>
							<Icon icon='edit' size='lg' color='blue.500' />
						</Button>
					)}

					<Button
						aria-label='Eliminar atributo'
						variant='ghost'
						colorScheme='red'
						size='sm'
						borderRadius='full'
						p='2'
						minW='auto'
						h='auto'
						onClick={() => deleteAttribute(attribute?._id ?? '')}
					>
						<Icon icon='delete' size='lg' color='red.500' />
					</Button>
				</Flex>
			</Flex>
		</ListItem>
	)
}

export default AttributeItem
